import { ArrowRight, Coffee, KeyRound, Wifi } from 'lucide-react'
import { Link } from 'react-router-dom'
import FaqList from '../components/FaqList'
import PageHero from '../components/PageHero'
import SEO from '../components/SEO'
import SectionHeading from '../components/SectionHeading'
import { localBusinessSchema } from '../config/schema'
import { imageSize } from '../data/images'

const suiteFaqs = [
  { question: 'What is a salon suite?', answer: 'A salon suite is a private workspace inside a larger beauty facility. It lets an independent professional set their own schedule, services, pricing, and atmosphere.' },
  { question: 'What amenities are included?', answer: 'Amenities may include high-speed Wi-Fi, utilities, common-area maintenance, laundry access, secure entry, a waiting area, coffee bar, and reception support.' },
  { question: 'Can I personalize my suite?', answer: 'Yes. Professionals are encouraged to decorate their suite in a way that reflects their brand while following the property guidelines.' },
  { question: 'How do I get started?', answer: 'Contact us to discuss the waitlist and pre-approval process. When a suitable suite becomes available, the team will review options and next steps with qualified applicants.' },
  { question: 'What types of professionals can lease a suite?', answer: 'Suites may be a fit for independent beauty and wellness professionals such as hairstylists, barbers, nail technicians, estheticians, lash and brow artists, massage therapists, makeup artists, tanning professionals, and related specialists. Contact the team to discuss your business and the space requirements for your specialty.' },
]

const amenities = [
  { image: 'amenity-chair.webp', title: 'Modern amenities', text: 'High-end furnishings, climate control, shared conveniences, and an elegant client environment.' },
  { image: 'amenity-space.webp', title: 'Customizable spaces', text: 'Create a suite that reflects your brand, specialty, and preferred client experience.' },
  { image: 'amenity-secure.webp', title: 'Secure access', text: 'Individual keypad access and security features support professionals working flexible schedules.' },
]

const suiteFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: suiteFaqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
}

const suiteStructuredData = [localBusinessSchema, suiteFaqSchema]

export default function Suites() {
  return (
    <>
      <SEO page="suites" structuredData={suiteStructuredData} />
      <PageHero index="03" eyebrow="Suites" title="Private salon suites in Ocala" description="Private, polished, and designed for independent beauty professionals." image="suite-interior.jpg" imagePosition="center 62%" />
      <section className="section suites-intro">
        <div className="container split-grid">
          <div>
            <SectionHeading index="01" eyebrow="Your space" title="A suite designed around the way you work" />
            <p>
              Each suite gives beauty professionals privacy, flexibility, and a refined foundation for building their business. Natural light, individual access, and customizable interiors make it possible to create a space that feels distinctly yours.
            </p>
            <p>
              Clients can enjoy the shared waiting area, coffee bar, convenient parking, and a professional environment from the moment they arrive.
            </p>
            <div className="inline-features">
              <span><Wifi /> High-speed Wi-Fi</span>
              <span><Coffee /> Coffee bar</span>
              <span><KeyRound /> Keypad access</span>
            </div>
          </div>
          <div className="image-frame">
            <img src={`${import.meta.env.BASE_URL}images/home-banner-slider-01-1.webp`} alt="Diamond Suites Ocala lounge" {...imageSize('home-banner-slider-01-1.webp')} loading="lazy" />
          </div>
        </div>
      </section>

      <section className="suite-manifesto" aria-label="Diamond Suites experience">
        <div className="container suite-manifesto__grid">
          <p><span>01</span><strong>Private space.</strong></p>
          <p><span>02</span><strong>Shared polish.</strong></p>
          <p><span>03</span><strong>Independent business.</strong></p>
        </div>
      </section>

      <section className="section suite-gallery-section section--soft" aria-labelledby="suite-gallery-title">
        <div className="container suite-gallery-container">
          <SectionHeading index="02" id="suite-gallery-title" eyebrow="The property" title="A balanced setting for focused, personal service" align="center" />
          <div className="suite-gallery">
            <figure>
              <img className="suite-gallery__suite" src={`${import.meta.env.BASE_URL}images/suite-interior.jpg`} alt="Private salon suite with styling chair and wash basin" {...imageSize('suite-interior.jpg')} loading="lazy" />
            </figure>
            <figure>
              <img className="suite-gallery__reception" src={`${import.meta.env.BASE_URL}images/Home-banner-slider.webp`} alt="Diamond Suites Ocala reception lounge" {...imageSize('Home-banner-slider.webp')} loading="lazy" />
            </figure>
            <figure>
              <img className="suite-gallery__lounge" src={`${import.meta.env.BASE_URL}images/directory-banner.webp`} alt="Shared lounge with equestrian artwork" {...imageSize('directory-banner.webp')} loading="lazy" />
            </figure>
            <figure>
              <img className="suite-gallery__exterior" src={`${import.meta.env.BASE_URL}images/community-main.jpg`} alt="Exterior entrance of Diamond Suites Ocala" {...imageSize('community-main.jpg')} loading="lazy" />
            </figure>
          </div>
          {/* <p className="suite-gallery__caption">
            Suite layouts and furnishings may vary. Each independent professional shapes their
            space around their own business and client experience.
          </p> */}
        </div>
      </section>

      <section className="section section--ink">
        <div className="container">
          <SectionHeading index="03" eyebrow="The experience" title="Built for focus, comfort, and independence" align="center" />
          <div className="amenity-grid">
            {amenities.map((amenity) => (
              <article key={amenity.title}>
                <img src={`${import.meta.env.BASE_URL}images/${amenity.image}`} alt="" {...imageSize(amenity.image)} loading="lazy" />
                <h3>{amenity.title}</h3>
                <p>{amenity.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--soft">
        <div className="container narrow-container">
          <SectionHeading index="04" eyebrow="Suite questions" title="What to know before leasing" align="center" />
          <FaqList items={suiteFaqs} />
        </div>
      </section>

      <section className="section waitlist-panel">
        <div className="container waitlist-panel__inner">
          <div>
            <p className="eyebrow">For beauty professionals</p>
            <h2>Suites are currently full, but the next opening could be yours.</h2>
            <p>Apply for pre-approval and join the waitlist for future availability.</p>
          </div>
          <Link className="button" to="/contact/">Join the waitlist <ArrowRight size={18} /></Link>
        </div>
      </section>
    </>
  )
}
